if (!customElements.get('liene-creator-videos')) {
  customElements.define('liene-creator-videos', class extends HTMLElement {
    source(data) {
      if (data.video) return { ...data, type: 'file', url: data.video };
      try {
        const url = new URL(data.social);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        const host = url.hostname.replace(/^www\./, '');
        if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)) {
          const id = host === 'youtu.be' ? url.pathname.split('/')[1] : url.searchParams.get('v') || url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1];
          if (/^[\w-]{11}$/.test(id || '')) return { ...data, type: 'youtube', id, url: `https://www.youtube.com/watch?v=${id}` };
        }
        if (host === 'instagram.com') {
          const match = url.pathname.match(/^\/(p|reel|reels|tv)\/([\w-]+)\/?$/);
          if (match) return { ...data, type: 'instagram', url: `https://www.instagram.com/${match[1] === 'reels' ? 'reel' : match[1]}/${match[2]}/` };
        }
      } catch (_) { /* Invalid or unset source: leave card disabled. */ }
      return null;
    }
    iframe(src, title, preview = false) {
      const frame = document.createElement('iframe');
      frame.src = src; frame.title = title; frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.allowFullscreen = true; frame.referrerPolicy = 'strict-origin-when-cross-origin';
      if (this.dataset.loadingMode) frame.loading = this.dataset.loadingMode === 'standard' ? 'eager' : 'lazy';
      if (preview) { frame.tabIndex = -1; frame.setAttribute('aria-hidden', 'true'); }
      return frame;
    }
    video(src, controls = false) {
      const video = document.createElement('video');
      video.src = src; video.playsInline = true; video.controls = controls; video.muted = !controls;
      video.loop = !controls; video.preload = 'metadata';
      return video;
    }
    youtube(source, preview) {
      const autoplay = preview || this.dataset.youtubeAutoplay !== 'false';
      const muted = preview || this.dataset.youtubeMuted === 'true';
      const controls = !preview && this.dataset.youtubeControls !== 'false';
      const loop = !preview && this.dataset.youtubeLoop === 'true';
      return `https://www.youtube-nocookie.com/embed/${source.id}?autoplay=${Number(autoplay)}&mute=${Number(muted)}&controls=${Number(controls)}&playsinline=1&rel=0&enablejsapi=1${loop ? `&loop=1&playlist=${source.id}` : ''}`;
    }
    connectedCallback() {
      this.abort?.abort(); this.resize?.disconnect(); this.visibility?.disconnect();
      this.abort = new AbortController(); const options = { signal: this.abort.signal };
      this.track = this.querySelector('.lcv__track'); this.cards = [...this.querySelectorAll('.lcv__card')];
      this.dialog = this.querySelector('dialog'); this.player = this.querySelector('.lcv__player');
      if (!this.track || !this.dialog) return;
      this.mobile = matchMedia('(max-width: 749px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => { this.cards.forEach(card => this.stop(card)); this.scheduleMobilePreview(); }, options);
      // Keep restored focus after closing without showing a keyboard ring for a tap.
      this.addEventListener('pointerdown', () => this.setAttribute('data-pointer-input', ''), options);
      document.addEventListener('keydown', () => this.removeAttribute('data-pointer-input'), options);
      window.addEventListener('message', event => {
        if (event.origin !== 'https://www.youtube-nocookie.com') return;
        let data;
        try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch (_) { return; }
        if (!data || data.event !== 'onStateChange') return;
        this.cards.forEach(card => {
          const frame = card.querySelector('.lcv__preview iframe');
          if (frame && frame.contentWindow === event.source) card.toggleAttribute('data-playing', data.info === 1);
        });
      }, options);
      this.cards.forEach(card => {
        card.querySelector('.lcv__play').addEventListener('click', () => this.open(card), options);
        card.addEventListener('pointerenter', () => this.hover(card), options);
        card.addEventListener('pointerleave', () => { if (!this.mobileAutoplay()) this.stop(card); }, options);
      });
      this.render = () => {
        this.cards.forEach(card => {
          this.stop(card, true);
          card.source = this.source(JSON.parse(this.mobile.matches ? card.dataset.mobile : card.dataset.desktop));
          card.querySelector('.lcv__play').disabled = !card.source;
          card.querySelector('.lcv__cover').replaceChildren();
          card.coverReady = false;
          if (card.visible || this.dataset.loadingMode === 'standard') this.cover(card);
        });
        this.scheduleMobilePreview();
      };
      this.mobile.addEventListener('change', this.render, options);
      this.visibility = new IntersectionObserver(entries => { entries.forEach(entry => {
        entry.target.visible = entry.isIntersecting;
        if (entry.isIntersecting) this.cover(entry.target); else this.stop(entry.target);
      }); this.scheduleMobilePreview(); }, { threshold: [0, 0.05] });
      this.nearby?.disconnect();
      if (this.fastPreview()) {
        this.nearby = new IntersectionObserver(() => this.scheduleMobilePreview(), { rootMargin: '300px 0px' });
        this.nearby.observe(this.track);
      }
      this.render(); this.cards.forEach(card => this.visibility.observe(card));
      const previous = this.querySelector('[data-direction="-1"]'), next = this.querySelector('[data-direction="1"]');
      this.update = () => { previous.disabled = Math.abs(this.track.scrollLeft) <= 2; next.disabled = Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2; this.scheduleMobilePreview(); };
      this.move = direction => {
        if (!this.cards.length) return;
        const style = getComputedStyle(this.track);
        this.track.scrollBy({ left: direction * (style.direction === 'rtl' ? -1 : 1) * (this.cards[0].getBoundingClientRect().width + parseFloat(style.columnGap)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      previous.addEventListener('click', () => this.move(-1), options); next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', this.update, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.move((event.key === 'ArrowRight' ? 1 : -1) * (getComputedStyle(this.track).direction === 'rtl' ? -1 : 1));
      }, options);
      this.resize = new ResizeObserver(this.update); this.resize.observe(this.track); this.update();
      this.querySelector('.lcv__close').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        if (this.dataset.playback === 'click') { this.dialog.close(); return; }
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => { this.clearPlayer(); this.opener?.focus({ preventScroll: true }); this.scheduleMobilePreview(); }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.cards.forEach(card => this.stop(card)); else this.scheduleMobilePreview(); }, options);
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.lcv__card'); if (!card) return;
        const style = getComputedStyle(this.track), r = card.getBoundingClientRect(), t = this.track.getBoundingClientRect(), pad = parseFloat(style.scrollPaddingInlineStart) || 0;
        this.track.scrollBy({ left: style.direction === 'rtl' ? r.right - t.right + pad : r.left - t.left - pad, behavior: 'instant' });
      }, options);
    }
    cover(card) {
      const source = card.source; if (!source || card.coverReady) return;
      card.coverReady = true;
      const container = card.querySelector('.lcv__cover');
      if (source.cover || source.type === 'youtube') {
        const image = document.createElement('img'); image.alt = ''; image.loading = this.dataset.loadingMode === 'standard' ? 'eager' : 'lazy';
        image.decoding = 'async';
        if (source.cover) {
          image.src = source.cover;
        } else {
          // Match the Pix-2 creator gallery: prefer the full-resolution poster.
          const qualities = ['maxresdefault', 'sddefault', 'hqdefault'];
          let quality = 0;
          const fallback = () => {
            if (quality >= qualities.length - 1) {
              image.onload = image.onerror = null;
              return;
            }
            image.src = `https://i.ytimg.com/vi/${source.id}/${qualities[++quality]}.jpg`;
          };
          image.onerror = fallback;
          // YouTube can return a small placeholder with a successful HTTP status.
          image.onload = () => {
            if (image.naturalWidth <= 120) fallback();
            else image.onload = image.onerror = null;
          };
          image.src = `https://i.ytimg.com/vi/${source.id}/${qualities[quality]}.jpg`;
        }
        container.append(image);
      } else if (source.type === 'file' || source.preview) {
        const video = this.video(source.type === 'file' ? source.url : source.preview);
        video.loop = false; video.setAttribute('aria-hidden', 'true');
        video.addEventListener('loadedmetadata', () => { if (video.duration > 0.01) video.currentTime = 0.01; }, { once: true });
        container.append(video);
      } else if (source.type === 'instagram') {
        container.append(this.iframe(`${source.url}embed/`, card.dataset.title, true));
      }
    }
    fastPreview() { return this.dataset.mobileAutoplay === 'leftmost'; }
    mobileAutoplay() {
      return this.dataset.mobileAutoplay === 'leftmost' && this.mobile.matches;
    }
    scheduleMobilePreview() {
      if (!this.fastPreview()) return;
      cancelAnimationFrame(this.previewFrame);
      this.previewFrame = requestAnimationFrame(() => { this.updateMobilePreview(); this.warmPreviews(); });
    }
    updateMobilePreview() {
      if (!this.isConnected || !this.mobileAutoplay()) return;
      const track = this.track.getBoundingClientRect();
      const left = Math.max(0, track.left), right = Math.min(innerWidth, track.right);
      const blocked = document.hidden || this.motion.matches || document.querySelector('liene-creator-videos dialog[open]');
      const candidates = blocked ? [] : this.cards.map(card => ({ card, rect: card.getBoundingClientRect() }))
        .filter(({ rect }) => rect.width > 0 && rect.height > 0 &&
          Math.min(rect.right, right) - Math.max(rect.left, left) > 1 &&
          Math.min(rect.bottom, track.bottom, innerHeight) - Math.max(rect.top, track.top, 0) > 1)
        .sort((a, b) => a.rect.left - b.rect.left);
      // At the end there is no room to align the penultimate card to the left.
      // Ignore the leftover slice of the previous card and play the first full card.
      const atEnd = Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2;
      const first = (atEnd && candidates.find(({ rect }) => rect.left >= left - 1 && rect.right <= right + 1) || candidates[0])?.card;
      if (first === this.mobileCard) return;
      this.cards.forEach(card => this.stop(card));
      this.mobileCard = first;
      if (first) { this.cover(first); this.preview(first); }
    }
    hover(card) {
      if (this.dataset.playback === 'click') return;
      const source = card.source;
      if (this.mobileAutoplay() || !source || this.dialog.open || !matchMedia('(hover: hover) and (pointer: fine)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.cards.forEach(item => this.stop(item));
      this.preview(card);
      this.warmPreviews();
    }
    preview(card) {
      const source = card.source;
      if (!source) return;
      const container = card.querySelector('.lcv__preview');
      card.previewRequested = true;
      if (source.type === 'file' || source.preview) {
        const video = this.prepareVideo(card);
        video.preload = 'auto';
        video.play().catch(() => { if (!card.previewRequested) return; card.removeAttribute('data-playing'); });
      } else if (source.type === 'youtube') {
        const frame = this.iframe(this.youtube(source, true), card.dataset.title, true);
        frame.addEventListener('load', () => {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'listening' }), 'https://www.youtube-nocookie.com');
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }), 'https://www.youtube-nocookie.com');
        }, { once: true });
        container.append(frame);
      }
    }
    prepareVideo(card) {
      const container = card.querySelector('.lcv__preview');
      let video = container.querySelector('video');
      if (video) return video;
      const source = card.source;
      if (!source || (source.type !== 'file' && !source.preview)) return null;
      video = this.video(source.type === 'file' ? (source.inlineVideo || source.url) : source.preview);
      video.preload = 'auto';
      if (source.cover) video.poster = source.cover;
      video.addEventListener('playing', () => {
        if (container.contains(video) && card.previewRequested && this.isConnected && !document.hidden) card.setAttribute('data-playing', '');
        else video.pause();
      });
      video.addEventListener('pause', () => { if (video.paused) card.removeAttribute('data-playing'); });
      video.addEventListener('error', () => { card.removeAttribute('data-playing'); video.remove(); });
      container.append(video);
      return video;
    }
    warmPreviews() {
      if (!this.fastPreview() || !this.isConnected) return;
      const rect = this.track.getBoundingClientRect();
      const active = this.cards.find(card => card.previewRequested);
      const keep = new Set(active ? [active] : []);
      if (!document.hidden && !this.motion.matches && !navigator.connection?.saveData &&
          !document.querySelector('liene-creator-videos dialog[open]') && rect.bottom > -300 && rect.top < innerHeight + 300) {
        const first = active || this.cards.find(card => {
          const r = card.getBoundingClientRect();
          return r.right > Math.max(0, rect.left) + 1 && r.left < Math.min(innerWidth, rect.right) - 1;
        });
        const index = this.cards.indexOf(first);
        if (index >= 0) this.cards.slice(Math.max(0, index - 1), index + 2).forEach(card => keep.add(card));
      }
      this.cards.forEach(card => {
        if (keep.has(card)) this.prepareVideo(card);
        else this.stop(card, true);
      });
    }
    stop(card, release = false) {
      if (this.mobileCard === card) this.mobileCard = null;
      card.previewRequested = false;
      card.removeAttribute('data-playing');
      const container = card.querySelector('.lcv__preview');
      container?.querySelectorAll('video').forEach(video => {
        video.pause();
        if (release || !this.fastPreview()) { video.removeAttribute('src'); video.load(); video.remove(); }
      });
      container?.querySelectorAll('iframe').forEach(frame => frame.remove());
    }
    clearPlayer() {
      this.player?.querySelectorAll('video').forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); });
      this.player?.replaceChildren();
    }
    open(card) {
      const source = card.source; if (!source) return;
      document.querySelectorAll('liene-creator-videos').forEach(section => {
        section.cards?.forEach(item => section.stop(item));
        if (section !== this && section.dialog?.open) section.dialog.close();
      });
      this.clearPlayer(); this.opener = card.querySelector('.lcv__play');
      this.dialog.setAttribute('aria-label', card.dataset.title);
      this.dialog.toggleAttribute('data-instagram', source.type === 'instagram');
      const link = this.querySelector('.lcv__external'); link.hidden = source.type === 'file' || this.dataset.playback === 'click'; link.href = source.url;
      if (source.type === 'file') {
        const video = this.video(source.url, true); if (source.cover) video.poster = source.cover;
        this.player.append(video); this.dialog.showModal(); video.play().catch(() => {});
      } else {
        this.player.append(this.iframe(source.type === 'youtube' ? this.youtube(source, false) : `${source.url}embed/`, card.dataset.title));
        this.dialog.showModal();
      }
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.previewFrame);
      this.nearby?.disconnect();
      this.abort?.abort(); this.resize?.disconnect(); this.visibility?.disconnect();
      this.cards?.forEach(card => this.stop(card, true)); this.clearPlayer(); if (this.dialog?.open) this.dialog.close();
    }
  });
}
